import React from 'react';
import { createStyles } from 'antd-style';

const useStyles = createStyles(({ token }) => ({
    messageContainer: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    avatarContainer: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 500,
        flexShrink: 0,
    },
    customerAvatar: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
    },
    cpAvatar: {
        backgroundColor: '#10b981',
        color: '#ffffff',
    },
    systemAvatar: {
        backgroundColor: '#6b7280',
        color: '#ffffff',
    },
    avatarPlaceholder: {
        width: '32px',
        marginRight: '8px',
    },
    messageColumn: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '320px',
        '@media (min-width: 1024px)': {
            maxWidth: '448px',
        },
    },
    senderLabel: {
        fontSize: '12px',
        color: token.colorTextSecondary,
        marginBottom: '4px',
        paddingLeft: '12px',
        paddingRight: '12px',
    },
    messageBubble: {
        padding: '8px 12px',
        borderRadius: '12px',
        wordBreak: 'break-word',
    },
    myMessageBubble: {
        backgroundColor: '#1677ff',
        color: '#ffffff',
    },
    otherMessageBubble: {
        backgroundColor: '#f5f5f5',
        color: '#262626',
    },
    imageContainer: {
        '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
            marginBottom: '4px',
            maxHeight: '200px',
        },
    },
    imageText: {
        fontSize: '14px',
        wordBreak: 'break-word',
    },
    messageContent: {
        wordBreak: 'break-word',
    },
    statusContainer: {
        padding: '0 4px',
    },
    statusText: {
        fontSize: '12px',
        opacity: 0.75,
        marginTop: '4px',
    },
    statusLine: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '4px',
    },
    statusLineEnd: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '4px',
        marginTop: '4px',
    },
    readStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
    },
    readStatusGreen: {
        color: '#52c41a',
    },
    readStatusBlue: {
        color: '#1677ff',
    },
    readStatusGray: {
        color: '#8c8c8c',
    },
    checkIcon: {
        width: '12px',
        height: '12px',
        fill: 'currentColor',
    },
}));

// 默认头像组件
const DefaultAvatar: React.FC<{ role: 'cp' | 'customer' | 'system'; styles: any }> = ({ role, styles }) => {
    const getAvatarText = () => {
        switch (role) {
            case 'customer': return '客';
            case 'cp': return '服';
            case 'system': return '系';
            default: return '?';
        }
    };

    const getAvatarStyle = () => {
        switch (role) {
            case 'customer': return styles.customerAvatar;
            case 'cp': return styles.cpAvatar;
            case 'system': return styles.systemAvatar;
            default: return styles.systemAvatar;
        }
    };

    return (
        <div className={`${styles.avatarContainer} ${getAvatarStyle()}`}>
            {getAvatarText()}
        </div>
    );
};

export interface ChatMessage {
    id?: string;
    orderNo: string;
    senderRole?: 'cp' | 'customer' | 'system';
    messageType: 'text' | 'image' | 'system';
    content: string;
    isRead?: boolean;
    readAt?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

interface MessageItemProps {
    message: ChatMessage;
    isMyMessage: boolean;
    index: number;
    previousMessage?: ChatMessage | null;
}

export const MessageItem: React.FC<MessageItemProps> = ({ message, isMyMessage, index, previousMessage }) => {
    const { styles } = useStyles();

    const formatTime = (timestamp: string) => {
        return new Date(timestamp).toLocaleTimeString('zh-CN', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSenderName = (message: ChatMessage) => {
        switch (message.senderRole) {
            case 'cp':
                return '码商';
            case 'customer':
                return '客户';
            case 'system':
                return '系统';
            default:
                return '未知';
        }
    };

    // 检查是否是连续消息（与前一条消息的发送者相同）
    const isContinuous = previousMessage && previousMessage.senderRole === message.senderRole;

    // 渲染已读状态标识
    const renderReadStatus = () => {
        // 客服发送的消息显示客户是否已读
        if (message.senderRole === 'cp') {
            return (
                <div className={isMyMessage ? styles.statusLineEnd : styles.statusLine}>
                    <span className={styles.statusText}>
                        {message?.createdAt && formatTime(message?.createdAt)}
                    </span>
                    <span className={styles.readStatus}>
                        {message.isRead ? (
                            <div className={styles.readStatusGreen}>
                                {/* 双勾图标表示已读 */}
                                <svg className={styles.checkIcon} viewBox="0 0 20 20">
                                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>已读</span>
                            </div>
                        ) : (
                            <div className={styles.readStatusBlue}>
                                {/* 单勾图标表示已发送但未读 */}
                                <svg className={styles.checkIcon} viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span>已送达</span>
                            </div>
                        )}
                    </span>
                </div>
            );
        }

        // 客户发送的消息只显示时间
        if (message.senderRole === 'customer') {
            return (
                <div className={styles.statusLine}>
                    <span className={styles.statusText}>
                        {message?.createdAt && formatTime(message?.createdAt)}
                    </span>
                </div>
            );
        }

        // 系统消息只显示时间
        return (
            <div className={styles.statusLine}>
                <span className={styles.statusText}>
                    {message?.createdAt && formatTime(message?.createdAt)}
                </span>
            </div>
        );
    };

    // 获取消息容器样式
    const getContainerStyle = (): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
        };

        if (index === 0) {
            baseStyle.marginTop = 0;
        } else if (isContinuous) {
            baseStyle.marginTop = '4px';
        } else {
            baseStyle.marginTop = '16px';
        }

        return baseStyle;
    };

    // 获取状态容器样式
    const getStatusStyle = (): React.CSSProperties => {
        return {
            textAlign: isMyMessage ? 'right' : 'left',
        };
    };

    return (
        <div className={styles.messageContainer} style={getContainerStyle()}>
            {/* 左侧头像（非当前用户消息且非连续消息时显示） */}
            {!isMyMessage && !isContinuous && (
                <div style={{ marginRight: '8px' }}>
                    <DefaultAvatar role={message.senderRole || 'system'} styles={styles} />
                </div>
            )}

            {/* 左侧头像占位（连续消息时） */}
            {!isMyMessage && isContinuous && (
                <div className={styles.avatarPlaceholder}></div>
            )}

            <div className={styles.messageColumn}>
                {/* 消息发送者标签（非当前用户消息且非连续消息时显示） */}
                {!isMyMessage && !isContinuous && (
                    <div className={styles.senderLabel}>
                        {getSenderName(message)}
                    </div>
                )}

                {/* 消息内容 */}
                <div className={`${styles.messageBubble} ${isMyMessage ? styles.myMessageBubble : styles.otherMessageBubble}`}>
                    {message.messageType === 'image' ? (
                        <div className={styles.imageContainer}>
                            <img
                                src={message.content}
                                alt="聊天图片"
                            />
                            <div className={styles.imageText}>图片</div>
                        </div>
                    ) : (
                        <div className={styles.messageContent}>{message.content}</div>
                    )}
                </div>

                {/* 时间戳和已读状态 */}
                <div className={styles.statusContainer} style={getStatusStyle()}>
                    {renderReadStatus()}
                </div>
            </div>

            {/* 右侧头像（当前用户消息且非连续消息时显示） */}
            {isMyMessage && !isContinuous && (
                <div style={{ marginLeft: '8px' }}>
                    <DefaultAvatar role={message.senderRole || 'cp'} styles={styles} />
                </div>
            )}

            {/* 右侧头像占位（连续消息时） */}
            {isMyMessage && isContinuous && (
                <div className={styles.avatarPlaceholder}></div>
            )}
        </div>
    );
}; 